import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { SEO } from "@/components/seo/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Loader2, CreditCard, ExternalLink, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription, isActive } from "@/hooks/useSubscription";
import { PLANS, formatUsd, planByPriceId } from "@/config/pricing";

const Billing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: subscription, isLoading, refetch } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);

  const justCheckedOut = searchParams.get("checkout") === "success";

  useEffect(() => {
    if (!justCheckedOut) return;
    toast.success("Payment received — activating your plan...");
    // Paddle webhooks land a moment after checkout completes.
    const timers = [3000, 8000].map((ms) => setTimeout(() => refetch(), ms));
    return () => timers.forEach(clearTimeout);
  }, [justCheckedOut, refetch]);

  const openPortal = async () => {
    try {
      setPortalLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth?redirect=/billing");
        return;
      }
      const { data, error } = await supabase.functions.invoke("paddle-portal", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const url = (data as any)?.url;
      if (error || !url) {
        throw new Error((data as any)?.error || error?.message || "Could not open the billing portal");
      }
      window.open(url, "_blank", "noopener");
    } catch (e: any) {
      console.error("Portal error:", e);
      toast.error(e.message || "Could not open the billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  const currentPlan =
    planByPriceId(subscription?.paddle_price_id) ??
    (subscription?.plan ? PLANS[subscription.plan as keyof typeof PLANS] : undefined);

  const active = isActive(subscription);

  return (
    <MainLayout>
      <SEO
        title="Billing — Veylo AI"
        description="View your Veylo AI plan, manage payment details, and cancel or upgrade your subscription."
        path="/billing"
      />
      <main className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Billing</h1>
          <p className="text-muted-foreground">Manage your Veylo AI subscription.</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" /> Current plan
                </CardTitle>
                <CardDescription>
                  {isLoading ? "Loading your subscription..." : active ? "Your subscription is active." : "You are on the free plan."}
                </CardDescription>
              </div>
              {!isLoading && (
                <Badge variant={active ? "default" : "secondary"}>
                  {subscription?.status ? subscription.status : "free"}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading...
              </div>
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-semibold">
                    {currentPlan ? currentPlan.name : "Free"}
                  </span>
                  {currentPlan && (
                    <span className="text-muted-foreground">{formatUsd(currentPlan.price)}/month</span>
                  )}
                </div>

                {subscription?.current_period_end && (
                  <p className="text-sm text-muted-foreground">
                    {subscription.cancel_at_period_end ? "Access ends on " : "Renews on "}
                    {new Date(subscription.current_period_end).toLocaleDateString()}
                  </p>
                )}

                {currentPlan && (
                  <>
                    <Separator />
                    <ul className="space-y-2">
                      {currentPlan.features
                        .filter((f) => f.included)
                        .map((f) => (
                          <li key={f.text} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-primary" />
                            {f.text}
                          </li>
                        ))}
                    </ul>
                  </>
                )}

                <div className="flex flex-wrap gap-3 pt-2">
                  {subscription?.paddle_subscription_id ? (
                    <Button onClick={openPortal} disabled={portalLoading}>
                      {portalLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ExternalLink className="w-4 h-4 mr-2" />
                      )}
                      Manage or cancel subscription
                    </Button>
                  ) : (
                    <Button onClick={() => navigate("/pricing")}>Choose a plan</Button>
                  )}
                  <Button variant="outline" onClick={() => navigate("/pricing")}>
                    Compare plans
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground">
          Payments, invoices and cancellations are handled securely by Paddle, our payment processor.
        </p>
      </main>
    </MainLayout>
  );
};

export default Billing;
